import React, { useState } from 'react';
import { Box, Button, Typography, useTheme as useMuiTheme, useMediaQuery } from '@mui/material';
import VirtualPATSidebar from './VirtualPATSidebar';
import ModernHealthcareHeader from './ModernHealthcareHeader.jsx';
import MobileBottomNavigation from './MobileBottomNavigation';
import { ChatInterface } from './Chat';
import { useEnterpriseAuth } from '../context/EnterpriseAuthContext';
import { useTheme } from '../context/ThemeContext';

const SIDEBAR_WIDTH = 280;
const SIDEBAR_WIDTH_COLLAPSED = 72;
const HEADER_HEIGHT = 64;
const MOBILE_BOTTOM_NAV_HEIGHT = 64;

function VirtualPATLayout({ children, user, handleLogout, stopImpersonation, appMode }) {
  const { user: authUser } = useEnterpriseAuth() || {};
  const effectiveUser = user || authUser || null;
  const muiTheme = useMuiTheme();
  const { mode, toggleColorMode } = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const isTablet = useMediaQuery(muiTheme.breakpoints.down('lg'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleToggleDarkMode = () => {
    toggleColorMode();
  };

  const handleReturnToWorkbench = () => {
    try {
      localStorage.setItem('useWorkbench', '1');
      const url = new URL(window.location.href);
      url.searchParams.delete('legacy');
      url.searchParams.set('useWorkbench', '1');
      window.location.href = url.toString();
    } catch {
      window.location.reload();
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        background: muiTheme.palette.background.gradient || muiTheme.palette.background.default,
      }}
    >
      {/* Sidebar - Always rendered but with different behavior on mobile */}
      <VirtualPATSidebar
        user={effectiveUser}
        onLogout={handleLogout}
        appMode={appMode}
        mobileOpen={mobileOpen}
        onDrawerToggle={handleDrawerToggle}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          transition: muiTheme.transitions.create(['margin'], {
            easing: muiTheme.transitions.easing.sharp,
            duration: muiTheme.transitions.duration.leavingScreen,
          }),
        }}
      >
        {/* Modern Header */}
        <ModernHealthcareHeader
          user={effectiveUser}
          onMenuClick={handleDrawerToggle}
          onLogout={handleLogout}
          darkMode={mode === 'dark'}
          onToggleDarkMode={handleToggleDarkMode}
          appMode={appMode}
        />

        {/* Main Content Area */}
        <Box
          sx={{
            flex: 1,
            position: 'relative',
            mt: `${HEADER_HEIGHT}px`,
            // Gradient background
            '&::before': {
              content: '""',
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: muiTheme.palette.gradients?.mesh || 'transparent',
              opacity: 0.03,
              pointerEvents: 'none',
              zIndex: 0,
            },
            // Prevent horizontal scroll
            '& > *': {
              maxWidth: '100%',
              overflowX: 'hidden',
              position: 'relative',
              zIndex: 1,
            },
          }}
        >
          {/* Add padding for content */}
          <Box
            sx={{
              pb: isMobile ? `${MOBILE_BOTTOM_NAV_HEIGHT}px` : 0, // Bottom navigation space
              minHeight: '100%',
              width: '100%',
            }}
          >
            <Box
              sx={{
                px: {
                  xs: 2,
                  sm: 3,
                  md: 3,
                },
                py: {
                  xs: 2,
                  sm: 3,
                  md: 3,
                },
                maxWidth: '100%',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  mb: 2,
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: 1,
                  border: theme => `1px solid ${theme.palette.warning.main}`,
                  backgroundColor: theme => theme.palette.warning.light,
                  color: theme => theme.palette.warning.contrastText,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, flex: '1 1 240px' }}>
                  You are viewing the legacy layout. Workbench is now the default experience.
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  color="inherit"
                  onClick={handleReturnToWorkbench}
                  sx={{ color: theme => theme.palette.warning.contrastText }}
                >
                  Switch to Workbench
                </Button>
              </Box>
              {children}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Mobile Sidebar Drawer */}
      {isMobile && (
        <VirtualPATSidebar
          user={effectiveUser}
          onLogout={handleLogout}
          appMode={appMode}
          mobileOpen={mobileOpen}
          onDrawerToggle={handleDrawerToggle}
        />
      )}

      {/* Mobile Bottom Navigation */}
      <MobileBottomNavigation appMode={appMode} onMenuClick={handleDrawerToggle} />
      
      {/* Chat Interface - Temporarily disabled */}
      {/* {appMode !== 'patient' && <ChatInterface />} */}
    </Box>
  );
}

export default VirtualPATLayout;
